/**
 * GSAP Scroll Reveal Library
 * 
 * Usage:
 * Add classes to elements to animate them on scroll:
 * - .scroll-fade-up, .scroll-fade-down, .scroll-fade-left, .scroll-fade-right
 * - .scroll-slide-up, .scroll-slide-down, .scroll-slide-left, .scroll-slide-right
 * - .scroll-scale, .scroll-scale-up, .scroll-scale-down
 * - .scroll-rotate-in
 * 
 * Optional data attributes:
 * - data-scroll-delay="0.2" - delay in seconds
 * - data-scroll-duration="0.8" - duration in seconds
 * - data-scroll-distance="60" - distance in pixels for slide animations
 * - data-scroll-start="top 80%" - ScrollTrigger start position
 * - data-scroll-stagger="0.1" - stagger delay for child elements
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

class ScrollReveal {
  constructor() {
    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (this.prefersReducedMotion) {
      this.disableAnimations();
      return;
    }

    try {
      document.documentElement.classList.add('has-scroll-reveal');
    } catch (_) {}

    // Animation configurations
    this.animations = {
      // Fade animations
      'scroll-fade-up': { opacity: 0, y: 30 },
      'scroll-fade-down': { opacity: 0, y: -30 },
      'scroll-fade-left': { opacity: 0, x: 30 },
      'scroll-fade-right': { opacity: 0, x: -30 },
      'scroll-fade': { opacity: 0 },
      
      // Slide animations (larger distance)
      'scroll-slide-up': { opacity: 0, y: 80 },
      'scroll-slide-down': { opacity: 0, y: -80 },
      'scroll-slide-left': { opacity: 0, x: 80 },
      'scroll-slide-right': { opacity: 0, x: -80 },
      
      // Scale animations
      'scroll-scale': { opacity: 0, scale: 0.8 },
      'scroll-scale-up': { opacity: 0, scale: 0.5 },
      'scroll-scale-down': { opacity: 0, scale: 1.2 },
      
      // Rotate animations
      'scroll-rotate-in': { opacity: 0, rotation: 10, scale: 0.9 },
      'scroll-rotate-left': { opacity: 0, rotation: -15 },
      'scroll-rotate-right': { opacity: 0, rotation: 15 },
      
      // Blur animation
      'scroll-blur': { opacity: 0, filter: 'blur(10px)' },
      
      // Clip animations
      'scroll-clip-left': { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
      'scroll-clip-right': { opacity: 0, clipPath: 'inset(0 0 0 100%)' },
      'scroll-clip-up': { opacity: 0, clipPath: 'inset(100% 0 0 0)' },
      'scroll-clip-down': { opacity: 0, clipPath: 'inset(0 0 100% 0)' },
      
      // Line reveal - special handling
      'scroll-line-reveal': { y: '100%' }
    };

    this.defaultOptions = {
      duration: 0.8,
      ease: 'power2.out',
      delay: 0,
      start: 'top 50%',
      end: 'bottom 20%',
      toggleActions: 'play none none none'
    };

    // Registry to manage state for line-reveal elements
    this.lineRevealRegistry = new Map();

    // Rebuild line splits on resize (debounced)
    // Only react to horizontal (width) changes to avoid mobile URL bar height resizes
    this._lastViewportWidth = window.innerWidth || document.documentElement.clientWidth;
    this._onResize = this.debounce(() => {
      const currentWidth = window.innerWidth || document.documentElement.clientWidth;
      if (currentWidth === this._lastViewportWidth) return;
      this._lastViewportWidth = currentWidth;
      this.rebuildLineReveals();
    }, 150);
    window.addEventListener('resize', this._onResize);

    this.init();
  }

  /**
   * Initialize all scroll reveal animations
   */
  init() {
    // Get all elements with scroll animation classes
    const animationClasses = Object.keys(this.animations);
    const selector = animationClasses.map(cls => `.${cls}`).join(', ');
    const elements = document.querySelectorAll(selector);

    if (elements.length === 0) return;

    const lineRevealElements = [];
    elements.forEach(element => {
      if (element.classList.contains('scroll-line-reveal')) {
        lineRevealElements.push(element);
      } else {
        this.createAnimation(element);
      }
    });

    let initFinished = false;
    const finishInit = () => {
      if (initFinished) return;
      initFinished = true;

      lineRevealElements.forEach(el => this.createAnimation(el));
      this.initFilterRefreshHook();

      // Signal dependent modules (e.g. overscroll) that line-reveal DOM is prepared.
      try {
        window.dispatchEvent(new CustomEvent('mohr-scroll-reveal:ready'));
      } catch (_) {}

      window.addEventListener('load', () => {
        ScrollTrigger.refresh();
        setTimeout(() => this.triggerVisibleElements(), 100);
      });

      setTimeout(() => this.triggerVisibleElements(), 100);
    };

    // Defer line-reveal until fonts are loaded to avoid wrong line breaks on initial load.
    // SplitType measures text dimensions; before fonts load, fallback fonts yield different
    // character widths, causing incorrect line splits.
    if (document.fonts && document.fonts.ready) {
      const timeout = setTimeout(finishInit, 3000);
      document.fonts.ready.then(() => {
        clearTimeout(timeout);
        finishInit();
      });
    } else {
      finishInit();
    }
  }

  /**
   * Refresh ScrollTrigger and trigger animations for elements that became visible
   * due to a filter action (without requiring an extra scroll event).
   */
  initFilterRefreshHook() {
    // Debounce refresh calls to avoid repeated heavy layout work.
    const refreshAfterFilter = this.debounce(() => {
      // Wait for layout to settle (styles/classes updated, elements reflowed).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ScrollTrigger.refresh();
          ScrollTrigger.update();
          this.triggerVisibleElements();
        });
      });
    }, 50);

    // CustomEvent in `_1_filter.js` does not bubble, so we attach directly.
    const filterGalleries = document.querySelectorAll('.js-filter');
    filterGalleries.forEach((el) => {
      el.addEventListener('filter-selection-updated', refreshAfterFilter);
    });
  }

  /**
   * Trigger animations for elements already visible in viewport
   */
  triggerVisibleElements() {
    const triggers = ScrollTrigger.getAll();
    
    triggers.forEach(trigger => {
      // Check if element is in viewport
      if (this.isInViewport(trigger.trigger)) {
        // Manually trigger the animation
        if (trigger.vars && trigger.vars.onEnter) {
          trigger.vars.onEnter();
        }
      }
    });
  }

  /**
   * Check if element is currently in viewport
   */
  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    // Element is visible if it intersects the viewport.
    // This is intentionally permissive so newly-filtered items animate immediately
    // when they enter the viewport (even without an extra scroll).
    return rect.top < windowHeight && rect.bottom > 0;
  }

  /**
   * Create animation for a single element
   */
  createAnimation(element) {
    // Find which animation class is applied
    const animationClass = Object.keys(this.animations).find(cls => 
      element.classList.contains(cls)
    );

    if (!animationClass) return;

    // Special handling for line reveal effect
    if (animationClass === 'scroll-line-reveal') {
      this.createLineRevealAnimation(element);
      return;
    }

    // Get animation config
    const fromVars = { ...this.animations[animationClass] };
    
    // Apply custom distance if specified
    const customDistance = element.getAttribute('data-scroll-distance');
    if (customDistance) {
      const distance = parseFloat(customDistance);
      if (fromVars.y !== undefined) fromVars.y = fromVars.y > 0 ? distance : -distance;
      if (fromVars.x !== undefined) fromVars.x = fromVars.x > 0 ? distance : -distance;
    }

    // Get custom options from data attributes
    const duration = parseFloat(element.getAttribute('data-scroll-duration')) || this.defaultOptions.duration;
    const delay = parseFloat(element.getAttribute('data-scroll-delay')) || this.defaultOptions.delay;
    const start = element.getAttribute('data-scroll-start') || this.defaultOptions.start;
    const stagger = element.getAttribute('data-scroll-stagger');
    const once = element.getAttribute('data-scroll-once') !== 'false'; // default true

    // Build animation options
    const toVars = {
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1,
      rotation: 0,
      filter: 'blur(0px)',
      //clipPath: 'inset(0 0 0 0)',
      duration: duration,
      ease: this.defaultOptions.ease,
      delay: delay,
      immediateRender: false,
      scrollTrigger: {
        trigger: element,
        start: start,
        end: this.defaultOptions.end,
        toggleActions: once ? 'play none none none' : 'play none none reverse',
        // markers: true // uncomment for debugging
      }
    };

    // Handle stagger for child elements
    if (stagger) {
      const children = element.children;
      if (children.length > 0) {
        // Ensure the parent itself stays visible.
        // We animate the children sequentially; hiding the parent would hide all children too.
        gsap.set(element, { opacity: 1 });

        // Apply initial state to children
        gsap.set(children, fromVars);
        
        toVars.stagger = parseFloat(stagger);
        toVars.immediateRender = false;
        delete toVars.scrollTrigger; // ScrollTrigger on parent element instead
        
        const animateChildrenIn = () => gsap.to(children, toVars);
        const animateChildrenOut = () => gsap.to(children, { ...fromVars, duration: duration / 2, stagger: 0 });
        
        ScrollTrigger.create({
          trigger: element,
          start: start,
          end: this.defaultOptions.end,
          onEnter: animateChildrenIn,
          onEnterBack: () => {
            if (!once) animateChildrenIn();
          },
          onLeave: () => {
            if (!once) animateChildrenOut();
          },
          onLeaveBack: () => {
            if (!once) animateChildrenOut();
          }
        });
        
        return;
      }
    }

    // Set initial state and animate
    gsap.set(element, fromVars);
    gsap.to(element, toVars);
  }

  /**
   * Create line reveal animation (text slides up from behind a "wall")
   */
  createLineRevealAnimation(element) {
    // Get custom options
    const duration = parseFloat(element.getAttribute('data-scroll-duration')) || 0.8;
    const delay = parseFloat(element.getAttribute('data-scroll-delay')) || this.defaultOptions.delay;
    const start = element.getAttribute('data-scroll-start') || this.defaultOptions.start;
    const stagger = parseFloat(element.getAttribute('data-scroll-stagger')) || 0.1;
    const once = element.getAttribute('data-scroll-once') !== 'false';

    // Destroy previous setup if any (e.g., after resize)
    this.destroyLineReveal(element);

    // Persist original HTML for robust revert in fallback cases
    if (!element.dataset.srOriginalHtml) {
      element.dataset.srOriginalHtml = element.innerHTML;
    }

    // Split text into lines
    const { lines, splitInstance } = this.splitIntoLines(element);
    
    if (lines.length === 0) return;

    // Wrap each line in overflow container
    lines.forEach(line => {
      const wrapper = document.createElement('div');
      wrapper.style.overflow = 'hidden';
      wrapper.style.display = 'block';
      
      const inner = document.createElement('div');
      inner.innerHTML = line.innerHTML;
      inner.style.display = 'block';
      
      // Set initial state
      gsap.set(inner, { y: '100%' });
      
      line.innerHTML = '';
      wrapper.appendChild(inner);
      line.appendChild(wrapper);
      
      line.classList.add('scroll-line-reveal__line');
    });

    // Get all inner elements to animate
    const inners = element.querySelectorAll('.scroll-line-reveal__line > div > div');

    // Animation function
    const animateIn = () => {
      gsap.to(inners, {
        y: '0%',
        duration: duration,
        ease: 'power3.out',
        stagger: stagger,
        delay: delay
      });
    };

    const animateOut = () => {
      gsap.to(inners, {
        y: '100%',
        duration: duration / 2,
        ease: 'power2.in',
        stagger: 0
      });
    };

    // Create scroll trigger animation
    const trigger = ScrollTrigger.create({
      trigger: element,
      start: start,
      end: this.defaultOptions.end,
      onEnter: animateIn,
      onEnterBack: () => {
        if (!once) animateIn();
      },
      onLeave: () => {
        if (!once) animateOut();
      },
      onLeaveBack: () => {
        if (!once) animateOut();
      }
    });

    // Save state for cleanup/rebuild
    this.lineRevealRegistry.set(element, {
      trigger,
      splitInstance: splitInstance || null
    });
  }

  /**
   * Split text content into lines based on natural line breaks
   */
  splitIntoLines(element) {
    // Prefer SplitType (npm import) for true visual line splitting
    if (SplitType) {
      try {
        const splitInstance = new SplitType(element, {
          types: 'lines',
          tagName: 'span'
        });
        // Return the actual line elements SplitType created
        return { lines: Array.from(splitInstance.lines || []), splitInstance };
      } catch (error) {
        // Fallback to manual splitting below if SplitType fails
      }
    }

    // Store original HTML
    const originalHTML = element.innerHTML;
    
    // Create a temporary element to measure
    const temp = element.cloneNode(true);
    temp.style.position = 'absolute';
    temp.style.visibility = 'hidden';
    temp.style.width = element.offsetWidth + 'px';
    document.body.appendChild(temp);

    // Get all text nodes
    const walker = document.createTreeWalker(
      temp,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      if (node.textContent.trim()) {
        textNodes.push(node);
      }
    }

    // Split by words and measure heights
    const lines = [];
    let currentLine = { y: -1, content: '' };
    
    element.childNodes.forEach((node, index) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent.split(/(\s+)/);
        words.forEach(word => {
          if (word.trim()) {
            currentLine.content += word;
          } else {
            currentLine.content += word;
          }
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const lineDiv = document.createElement('div');
        lineDiv.innerHTML = node.outerHTML;
        lineDiv.style.display = 'block';
        lines.push(lineDiv);
      }
    });

    document.body.removeChild(temp);

    // If simple split didn't work, wrap entire content
    if (lines.length === 0) {
      // Wrap each block-level child or create single line
      const children = Array.from(element.children);
      if (children.length > 0) {
        return { lines: children.map(child => {
          const lineDiv = document.createElement('div');
          lineDiv.appendChild(child.cloneNode(true));
          return lineDiv;
        }), splitInstance: null };
      } else {
        const lineDiv = document.createElement('div');
        lineDiv.innerHTML = element.innerHTML;
        element.innerHTML = '';
        element.appendChild(lineDiv);
        return { lines: [lineDiv], splitInstance: null };
      }
    }

    return { lines, splitInstance: null };
  }

  /**
   * Destroy line reveal setup for an element (unwrap and revert)
   */
  destroyLineReveal(element) {
    const state = this.lineRevealRegistry.get(element);
    if (state && state.trigger) {
      try { state.trigger.kill(); } catch (_) {}
    }

    // Unwrap our inner wrappers inside the current line elements if present
    const currentLines = element.querySelectorAll('.scroll-line-reveal__line');
    if (currentLines.length) {
      currentLines.forEach(line => {
        const inner = line.querySelector(':scope > div > div');
        if (inner) {
          line.innerHTML = inner.innerHTML;
        }
        line.classList.remove('scroll-line-reveal__line');
      });
    }

    // Revert SplitType if used, otherwise restore original HTML if available
    if (state && state.splitInstance && typeof state.splitInstance.revert === 'function') {
      try { state.splitInstance.revert(); } catch (_) {}
    } else if (element.dataset && element.dataset.srOriginalHtml) {
      element.innerHTML = element.dataset.srOriginalHtml;
    }

    if (state) {
      this.lineRevealRegistry.delete(element);
    }
  }

  /**
   * Rebuild all line reveal elements (e.g., on resize)
   */
  rebuildLineReveals() {
    const elements = document.querySelectorAll('.scroll-line-reveal');
    if (!elements.length) return;
    elements.forEach(el => {
      this.createLineRevealAnimation(el);
    });
    ScrollTrigger.refresh();
  }

  /**
   * Simple debounce helper
   */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Disable all animations (for reduced motion)
   */
  disableAnimations() {
    const animationClasses = Object.keys(this.animations);
    const selector = animationClasses.map(cls => `.${cls}`).join(', ');
    const elements = document.querySelectorAll(selector);

    elements.forEach(element => {
      gsap.set(element, { 
        opacity: 1, 
        y: 0, 
        x: 0, 
        scale: 1, 
        rotation: 0,
        filter: 'none',
        clipPath: 'none'
      });
    });
  }

  /**
   * Refresh all ScrollTriggers (useful after DOM changes)
   */
  static refresh() {
    ScrollTrigger.refresh();
  }

  /**
   * Kill all ScrollTriggers
   */
  static destroy() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ScrollReveal();
  });
} else {
  new ScrollReveal();
}

// Export for manual initialization
export default ScrollReveal;

